import { useState } from 'react';
import type { CreateExerciseInput, Exercise, Equipment, CommonMistake } from '../services/types';
import { getImageUrl } from '../services/api';

const emptyFormData: CreateExerciseInput = {
  name: '',
  description: '',
  category: '',
  stimulusToFatigueRatio: '5.00',
  equipment: '',
  muscleGroup: '',
  primaryMuscleGroup: '',
  secondaryMuscleGroup: '',
  tertiaryMuscleGroup: '',
  instructions: '',
  difficulty: 'beginner',
  anatomyImages: [],
  instructionImages: [],
  youtubeVideoUrl: '',
  cautionNotes: '',
  commonMistakes: [],
  unilateral: false,
  isometric: false,
};

export function useExerciseForm() {
  const [formData, setFormData] = useState<CreateExerciseInput>({ ...emptyFormData });
  const [newAnatomyImage, setNewAnatomyImage] = useState('');
  const [newInstructionImage, setNewInstructionImage] = useState('');
  const [uploadingAnatomy, setUploadingAnatomy] = useState(false);
  const [uploadingInstruction, setUploadingInstruction] = useState(false);
  const [uploadingMistakeImage, setUploadingMistakeImage] = useState<number | null>(null);

  const resetForm = () => {
    setFormData({ ...emptyFormData });
    setNewAnatomyImage('');
    setNewInstructionImage('');
  };

  const populateFromExercise = (exercise: Exercise) => {
    let equipmentEntityIRI: string | null = null;
    if (exercise.equipmentEntity) {
      if (typeof exercise.equipmentEntity === 'string') {
        equipmentEntityIRI = exercise.equipmentEntity;
      } else if (exercise.equipmentEntity.id) {
        equipmentEntityIRI = `/api/equipment/${exercise.equipmentEntity.id}`;
      }
    }

    setFormData({
      name: exercise.name,
      description: exercise.description || '',
      category: exercise.category,
      stimulusToFatigueRatio: exercise.stimulusToFatigueRatio || '5.00',
      equipment: exercise.equipment,
      equipmentEntity: equipmentEntityIRI,
      muscleGroup: exercise.muscleGroup,
      primaryMuscleGroup: exercise.primaryMuscleGroup || '',
      secondaryMuscleGroup: exercise.secondaryMuscleGroup || '',
      tertiaryMuscleGroup: exercise.tertiaryMuscleGroup || '',
      instructions: exercise.instructions || '',
      difficulty: exercise.difficulty,
      anatomyImages: exercise.anatomyImages || [],
      instructionImages: exercise.instructionImages || [],
      youtubeVideoUrl: exercise.youtubeVideoUrl || '',
      cautionNotes: exercise.cautionNotes || '',
      commonMistakes: (exercise.commonMistakes || []).map((m: any) =>
        typeof m === 'string' ? { description: m, images: [] } : m
      ),
      unilateral: exercise.unilateral || false,
      isometric: exercise.isometric || false,
    });
  };

  const handleAddAnatomyImage = () => {
    if (newAnatomyImage.trim()) {
      setFormData({
        ...formData,
        anatomyImages: [...(formData.anatomyImages || []), newAnatomyImage.trim()],
      });
      setNewAnatomyImage('');
    }
  };

  const handleRemoveAnatomyImage = (index: number) => {
    setFormData({
      ...formData,
      anatomyImages: (formData.anatomyImages || []).filter((_, i) => i !== index),
    });
  };

  const handleAddInstructionImage = () => {
    if (newInstructionImage.trim()) {
      setFormData({
        ...formData,
        instructionImages: [...(formData.instructionImages || []), newInstructionImage.trim()],
      });
      setNewInstructionImage('');
    }
  };

  const handleRemoveInstructionImage = (index: number) => {
    setFormData({
      ...formData,
      instructionImages: (formData.instructionImages || []).filter((_, i) => i !== index),
    });
  };

  const uploadImage = async (file: File): Promise<string | null> => {
    const uploadData = new FormData();
    uploadData.append('image', file);
    const response = await fetch(`https://${window.location.hostname}/api/upload/exercise-image`, {
      method: 'POST',
      body: uploadData,
    });
    const data = await response.json();
    return data.success ? data.url : null;
  };

  const handleAnatomyImageUpload = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file) return;

    setUploadingAnatomy(true);
    try {
      const url = await uploadImage(file);
      if (url) {
        setFormData((prev) => ({
          ...prev,
          anatomyImages: [...(prev.anatomyImages || []), url],
        }));
      }
    } catch (err) {
      console.error('Failed to upload anatomy image:', err);
    } finally {
      setUploadingAnatomy(false);
      event.target.value = '';
    }
  };

  const handleInstructionImageUpload = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file) return;

    setUploadingInstruction(true);
    try {
      const url = await uploadImage(file);
      if (url) {
        setFormData((prev) => ({
          ...prev,
          instructionImages: [...(prev.instructionImages || []), url],
        }));
      }
    } catch (err) {
      console.error('Failed to upload instruction image:', err);
    } finally {
      setUploadingInstruction(false);
      event.target.value = '';
    }
  };

  const handleAddMistake = () => {
    setFormData((prev) => ({
      ...prev,
      commonMistakes: [...(prev.commonMistakes || []), { description: '', images: [] }],
    }));
  };

  const handleRemoveMistake = (index: number) => {
    setFormData((prev) => ({
      ...prev,
      commonMistakes: (prev.commonMistakes || []).filter((_, i) => i !== index),
    }));
  };

  const handleMistakeDescriptionChange = (index: number, description: string) => {
    setFormData((prev) => {
      const mistakes = [...(prev.commonMistakes || [])];
      mistakes[index] = { ...mistakes[index], description };
      return { ...prev, commonMistakes: mistakes };
    });
  };

  const handleMistakeImageUpload = async (mistakeIndex: number, event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file) return;

    setUploadingMistakeImage(mistakeIndex);
    try {
      const url = await uploadImage(file);
      if (url) {
        setFormData((prev) => {
          const mistakes = [...(prev.commonMistakes || [])];
          mistakes[mistakeIndex] = {
            ...mistakes[mistakeIndex],
            images: [...mistakes[mistakeIndex].images, url],
          };
          return { ...prev, commonMistakes: mistakes };
        });
      }
    } catch (err) {
      console.error('Failed to upload mistake image:', err);
    } finally {
      setUploadingMistakeImage(null);
      event.target.value = '';
    }
  };

  const handleRemoveMistakeImage = (mistakeIndex: number, imageIndex: number) => {
    setFormData((prev) => {
      const mistakes = [...(prev.commonMistakes || [])];
      mistakes[mistakeIndex] = {
        ...mistakes[mistakeIndex],
        images: mistakes[mistakeIndex].images.filter((_, i) => i !== imageIndex),
      };
      return { ...prev, commonMistakes: mistakes };
    });
  };

  return {
    formData,
    setFormData,
    newAnatomyImage,
    setNewAnatomyImage,
    newInstructionImage,
    setNewInstructionImage,
    uploadingAnatomy,
    uploadingInstruction,
    uploadingMistakeImage,
    resetForm,
    populateFromExercise,
    handleAddAnatomyImage,
    handleRemoveAnatomyImage,
    handleAddInstructionImage,
    handleRemoveInstructionImage,
    handleAnatomyImageUpload,
    handleInstructionImageUpload,
    handleAddMistake,
    handleRemoveMistake,
    handleMistakeDescriptionChange,
    handleMistakeImageUpload,
    handleRemoveMistakeImage,
  };
}
